


















































import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useDarkModeController from "@/hooks/useDarkModeController";
import { darkModeReplacePostfix } from "@/lib/darkModeReplacePostfix";
import { CheckRole } from "@/lib/decorators";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BaseIcon from "@/components/BaseIcon";
import BaseInputFile from "@/components/BaseInputFile/BaseInputFile.vue";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";
import ProfileHeaderAvatar from "@/components/ProfileHeaderAvatar";
import ProfileHeaderBackground from "@/components/ProfileHeaderBackground";
import SelectUserTagsModal from "@/components/SelectUserTagsModal";
import ImageUpload from "./ImageUpload.vue";
import ProfileTagsView from "./ProfileTagsView.vue";

@Component({
  name: "PageProfileEditView",
  components: {
    SelectUserTagsModal,
    ProfileTagsView,
    ImageUpload,
    BaseInputFile,
    ProfileHeaderAvatar,
    ProfileHeaderBackground,
    BaseForm,
    BasePage,
    BaseIcon,
    PageTitle,
    BaseButton,
  },
  setup() {
    const { isDarkMode } = useDarkModeController();

    return { isDarkMode };
  },
})
export default class PageProfileEditView extends Vue {
  @Prop({ required: true })
  public readonly user!: UserPublicData;

  @Prop({ required: true })
  public readonly info!: Record<string, string>;

  @Prop({ required: true })
  public readonly files!: FileData[];

  @Prop({ type: Boolean })
  public loading!: boolean;

  @Prop({ type: Array })
  public userTags!: TagInfo[] | undefined;

  @CheckRole("member")
  public isMember!: boolean;

  public isDarkMode!: ReturnType<typeof useDarkModeController>["isDarkMode"]["value"];

  public showUserTagsModal: boolean = false;

  public isValid: boolean = true;

  public userFieldsValues: FormValues = {};

  public socialFieldsValues: FormValues = {};

  public amazonValues: FormValues = {};

  public get restrictedMember() {
    return config.featureRestrictMember && this.user.role === "member";
  }

  public get isCreator() {
    return checkRole(this.user.role, "creator");
  }

  public get allowThemes() {
    return config.featureTags;
  }

  public get avatar() {
    return this.getImageUrl("avatar");
  }

  public get background() {
    return this.getImageUrl("background_image");
  }

  public get questionaryUserFields() {
    const fields = getQuestionaryInfoFields().filter(i => !i.fieldMeta?.social && !i.fieldMeta?.amazon);
    return this.translateLabelsInFields(fields);
  }

  public get socialFields(): FormFields {
    const fields = this.restrictedMember ? [] : getQuestionaryInfoFields().filter(i => i.fieldMeta?.social);
    return this.translateLabelsInFields(
      fields.map(i =>
        !!i.appendIcon ? { ...i, appendIcon: darkModeReplacePostfix(i.appendIcon, this.isDarkMode) } : i
      )
    );
  }

  public get amazonFields(): FormFields {
    const fields = this.isCreator ? getQuestionaryInfoFields().filter(i => i.fieldMeta?.amazon) : [];
    return this.translateLabelsInFields(fields);
  }

  get userFields(): FormFields {
    const fields = this.restrictedMember
      ? this.questionaryUserFields.filter(i => i.fieldMeta?.allowedForMember)
      : this.questionaryUserFields;
    return [
      {
        name: "screenName",
        label: $t("Name"),
        rules: ["required", "max:30"],
        maxlength: "30",
      },
      {
        name: "username",
        label: $t("Username"),
        rules: {
          required: true,
          regex: /^[a-zA-Z0-9_.-]*$/,
          max: 30,
        },
        maxlength: "30",
        rulesMessages: {
          regex: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
        },
        hint: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
      },
      ...fields,
    ];
  }

  public translateLabelsInFields(fields: FormFields): FormFields {
    return fields.map(i => ({ ...i, label: i.label && $t(i.label) }));
  }

  public onUserTagsSave(tags: string[]) {
    this.showUserTagsModal = false;
    this.$emit("updateUserTags", tags);
  }

  public getImageUrl(type: string) {
    const file = this.files.find(file => file.fileId.type === type && file.fileId.subjectId === this.user.id);
    return file ? MediaFilesController.getImageThumbnail(file.urlPart, "1080p") : "";
  }

  public onValidation(isValid: boolean) {
    this.isValid = isValid;
  }

  public save() {
    this.$emit("save", { ...this.userFieldsValues, ...this.socialFieldsValues, ...this.amazonValues });
  }

  public created() {
    this.userFieldsValues = {
      screenName: this.user.screenName,
      username: this.user.username,
      ...this.questionaryUserFields.reduce<FormValues>((out, item) => {
        out[item.name] = this.info[item.name];
        return out;
      }, {}),
    };
    this.socialFieldsValues = {
      ...this.socialFields.reduce<FormValues>((out, item) => {
        out[item.name] = this.info[item.name];
        return out;
      }, {}),
    };
    this.amazonValues = {
      ...this.amazonFields.reduce<FormValues>((out, item) => {
        out[item.name] = this.info[item.name];
        return out;
      }, {}),
    };
  }
}
